import { useCallback, useEffect, useState } from "react";

import ActionButton from "@/components/Button/ActionButton";
import Icon from "@/components/Icon/Icon";
import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";

type LabelPreviewDialogProps = {
  labelId: string | null;
  labelImageUrl?: string | null;
  isOpen: boolean;
  onClose: () => void;
};

export function LabelPreviewDialog({
  labelId,
  labelImageUrl: propLabelImageUrl,
  isOpen,
  onClose,
}: LabelPreviewDialogProps) {
  const [labelImageUrl, setLabelImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation(defaultNS, {
    keyPrefix: "domain.device.inventoryLabel",
  });

  // ダイアログが開かれた時に画像URLを設定
  useEffect(() => {
    if (isOpen && propLabelImageUrl) {
      setLabelImageUrl(propLabelImageUrl);
      setIsLoading(false);
      setError(null);
    } else if (isOpen && labelId && !propLabelImageUrl) {
      // フォールバック: labelIdのみが提供された場合の処理
      setIsLoading(true);
      setError(null);
      setLabelImageUrl(
        // TODO: 自前で用意する
        "https://dummyimage.com/127x127/000000/FFFFFF&text=QR+Code",
      );
      setIsLoading(false);
    } else {
      setLabelImageUrl(null);
      setError(null);
    }
  }, [isOpen, labelId, propLabelImageUrl]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        // TODO: スタイル指定修正
        className={cn(
          "w-[560px] p-6 bg-[#FCFCFD] border border-[#8D8D91] rounded-lg",
          "shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]",
        )}
        hideCloseButton
      >
        <VStack className="gap-4">
          {/* ヘッダー部分 */}
          <HStack className="w-full justify-between items-start">
            <VStack className="gap-1.5 flex-1">
              <Text
                variant="h3"
                // TODO: スタイル指定修正
                className={cn(
                  "text-[#1F1F22] font-semibold text-[18px] leading-[1.5em] text-left",
                )}
              >
                {t("labelPreviewDialog.title")}
              </Text>
            </VStack>

            {/* 閉じるボタン */}
            <div className="p-1.5">
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="w-10 h-10 p-2.5 rounded-md"
              >
                <Icon iconName="XIcon" className="w-4 h-4 text-[#646467]" />
              </ActionButton>
            </div>
          </HStack>

          {/* QRコード表示エリア */}
          <div
            className={cn(
              "w-[339px] h-[271px] mx-auto",
              "border border-[rgba(0,8,44,0.9)] rounded p-3",
              "flex flex-col justify-center items-center gap-3",
            )}
          >
            {/* QRコード画像 */}
            <div className="w-[127px] h-[127px] flex items-center justify-center">
              {isLoading ? (
                <div className="animate-pulse bg-gray-200 w-full h-full rounded" />
              ) : error ? (
                <div className="w-full h-full bg-gray-100 rounded flex items-center justify-center">
                  <Text variant="labelSmall" className="text-gray-500">
                    Failed to load
                  </Text>
                </div>
              ) : labelImageUrl ? (
                <img
                  src={labelImageUrl}
                  alt="QR Code"
                  className="w-full h-full object-cover rounded"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 rounded flex items-center justify-center">
                  <Text variant="labelSmall" className="text-gray-500">
                    No image
                  </Text>
                </div>
              )}
            </div>
          </div>
        </VStack>
      </DialogContent>
    </Dialog>
  );
}
